import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Scissors,
  Loader2,
  ArrowRight,
  FileText,
  AlertCircle,
  Check,
  FileArchive
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FileSplitterPage = () => {
  const [file, setFile] = useState(null);
  const [rowsPerFile, setRowsPerFile] = useState(1000);
  const [error, setError] = useState('');
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const { user } = useAuth();

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some(e => e.code === 'file-too-large')) {
        toast.error('File too large. Maximum size is 50MB.');
      } else {
        toast.error('Invalid file type. Please upload CSV or Excel files.');
      }
      return;
    }
    
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      // Additional check for 50MB
      if (selectedFile.size > 50 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 50MB.');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSplitClick = () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    if (rowsPerFile < 10 || rowsPerFile > 100000) {
      toast.error('Rows per file must be between 10 and 100,000');
      return;
    }

    // For logged-in users, download directly
    if (user?.email) {
      downloadAsZip();
    } else {
      // For anonymous users, show email capture modal first
      setShowEmailModal(true);
    }
  };

  const downloadAsZip = async () => {
    setDownloadingZip(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/tools/split-file?rows_per_file=${rowsPerFile}&output_format=zip`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
          timeout: 300000 // 5 minute timeout for large files
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let zipFilename = `${file.name.replace(/\.[^/.]+$/, '')}_split.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) zipFilename = match[1];
      }
      
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Get metadata from headers
      const totalRows = response.headers['x-total-rows'] || 'N/A';
      const numFiles = response.headers['x-num-files'] || 'N/A';

      toast.success(`Downloaded ZIP with ${numFiles} files (${totalRows} total rows)`);
    } catch (err) {
      console.error('ZIP download error:', err);
      let errorMsg = 'Failed to create ZIP file';
      if (err.response?.data) {
        // Try to read error from blob
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Ignore parse error
        }
      }
      toast.error(errorMsg);
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Scissors className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-3">
            Free CSV File Splitter
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Split large CSV or Excel files into smaller chunks. Supports files up to <strong>50MB</strong>. No login required.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8">
          {/* Upload Zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              isDragActive 
                ? 'border-indigo-500 bg-indigo-50' 
                : file 
                  ? 'border-emerald-300 bg-emerald-50' 
                  : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
            }`}
          >
            <input {...getInputProps()} data-testid="file-input" />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <Check className="w-6 h-6 text-emerald-500" />
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-700 font-medium mb-1">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}
                </p>
                <p className="text-sm text-slate-500">or click to browse (CSV, Excel up to 50MB)</p>
              </>
            )}
          </div>

          {/* Settings */}
          <div className="mt-6">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Rows per file
            </Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={rowsPerFile}
                onChange={(e) => setRowsPerFile(parseInt(e.target.value) || 1000)}
                min={10}
                max={100000}
                className="w-40"
                data-testid="rows-per-file"
              />
              <span className="text-sm text-slate-500">
                Min: 10 | Max: 100,000
              </span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Action Buttons - Single CTA */}
          <div className="mt-6">
            <Button
              onClick={handleSplitClick}
              disabled={!file || downloadingZip}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg"
              data-testid="download-zip-btn"
            >
              {downloadingZip ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating ZIP...
                </>
              ) : (
                <>
                  <FileArchive className="w-5 h-5 mr-2" />
                  Split &amp; Download ZIP
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <p className="font-medium text-slate-900">Up to 50MB</p>
            <p className="text-sm text-slate-500">Large file support</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <FileArchive className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="font-medium text-slate-900">ZIP Download</p>
            <p className="text-sm text-slate-500">All files in one click</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Check className="w-5 h-5 text-purple-600" />
            </div>
            <p className="font-medium text-slate-900">100% Free</p>
            <p className="text-sm text-slate-500">No login required</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <p className="text-slate-600 mb-4">
            Need more powerful data cleaning features?
          </p>
          <Link to="/login">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white">
              Try CRM Data Cleaner Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>
      
      {/* Email Capture Modal for Anonymous Users */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={() => {
          setShowEmailModal(false);
          downloadAsZip();
        }}
        tool="file-splitter"
        fileId={file?.name || 'split'}
        toolName="File Splitter"
        accentColor="indigo"
      />
      
      {/* Floating Support Button */}
      <SupportButton />
    </div>
  );
};

export default FileSplitterPage;
